import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PlotlyChartActions from 'components/Plotly/PlotlyChartActions';
import ThemedPlotlyFigure from 'components/Plotly/ThemedPlotlyFigure';
import { groupBy, isEmpty, join, map, size } from 'es-toolkit/compat';
import Markdown from 'markdown-to-jsx';
import React from 'react';

import { AgentStreamAction, AgentStreamProgress, SectionTable } from 'interfaces/agentSession';
import { titleCase } from 'utils/helpers';
import { resolveOriginTemplate } from 'utils/resolveOriginTemplate';
import { getActionVisual, getReadableProgressSummary } from './helpers';
import { actionCardSx, actionDetailMarkdownSx, actionListSx, actionRowSx, markdownOverrides } from './style';

const UNDERSCORE_REGEX = /_/g;

type DisplayAgentAction = AgentStreamAction & {
  occurrenceCount: number;
};

export const getParallelLaneTitle = (action: AgentStreamAction) => {
  if (action.kind === 'agent' || action.kind === 'node') {
    return titleCase(action.name.replace(UNDERSCORE_REGEX, ' '));
  }
  const visual = getActionVisual(action);
  return visual.label;
};

export const splitAgentActionGroups = <T extends { status: AgentStreamAction['status'] }>(actions: T[]) => {
  const actionsByStatus = groupBy(actions, 'status') as Partial<Record<AgentStreamAction['status'], T[]>>;
  return {
    runningActions: actionsByStatus['running'] || [],
    completedActions: actionsByStatus['completed'] || [],
  };
};

const getActionDeduplicationKey = (action: AgentStreamAction) =>
  join([action.kind, action.name, action.label, action.detail || '', action.status], '::');

const collapseRepeatedActions = (actions: AgentStreamAction[]): DisplayAgentAction[] => {
  const orderedKeys: string[] = [];
  const groupedActions = new Map<string, DisplayAgentAction>();

  for (const action of actions) {
    const key = getActionDeduplicationKey(action);
    const existingAction = groupedActions.get(key);

    if (existingAction) {
      existingAction.occurrenceCount += 1;
      continue;
    }

    orderedKeys.push(key);
    groupedActions.set(key, { ...action, occurrenceCount: 1 });
  }

  return map(orderedKeys, (key) => groupedActions.get(key) as DisplayAgentAction);
};

const expandActionsForStreaming = (actions: AgentStreamAction[]): DisplayAgentAction[] =>
  map(actions, (action) => ({ ...action, occurrenceCount: 1 }));

export const getAgentActionSummary = (actions: AgentStreamAction[], isStreaming = false) => {
  const totalCount = size(actions);
  const { runningActions, completedActions } = splitAgentActionGroups(actions);
  const completedCount = size(completedActions);
  const runningCount = size(runningActions);

  let statusText = 'Waiting for the next action';
  if (runningCount > 1) {
    statusText = `${runningCount} actions running in parallel`;
  } else if (runningCount === 1) {
    statusText = '1 action running';
  } else if (isStreaming && totalCount > 0 && completedCount === totalCount) {
    statusText = 'Drafting response';
  } else if (!isStreaming && totalCount > 0 && completedCount === totalCount) {
    statusText = 'All actions completed';
  }

  return {
    totalCount,
    completedCount,
    runningCount,
    statusText,
  };
};

const AgentActionIcon: React.FC<{ action: AgentStreamAction }> = ({ action }) => {
  const theme = useTheme();
  const visual = getActionVisual(action);
  const iconColor = action.status === 'completed' ? theme.palette.success.main : visual.color(theme);
  const backgroundColor =
    action.status === 'completed' ? alpha(theme.palette.success.main, 0.12) : alpha(visual.color(theme), 0.12);
  const IconComponent = visual.Icon;

  return (
    <Box
      role="img"
      aria-label={visual.label}
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1.5,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: backgroundColor,
        flexShrink: 0,
      }}
    >
      <IconComponent sx={{ fontSize: 16, color: iconColor }} />
    </Box>
  );
};

const AgentChartPreview: React.FC<{ action: DisplayAgentAction }> = ({ action }) => {
  const chartPreview = action.chartPreview;
  const [chartEl, setChartEl] = React.useState<HTMLElement | null>(null);

  if (!chartPreview?.plotlyFigure) {
    return null;
  }

  const { plotlyFigure, title } = chartPreview;

  // Apply compact layout overrides for the mini preview.
  const compactFigure = React.useMemo(
    () => ({
      data: plotlyFigure.data,
      layout: {
        ...plotlyFigure.layout,
        autosize: true,
        margin: { l: 28, r: 12, t: 24, b: 28, pad: 0 },
        showlegend: false,
      },
    }),
    [plotlyFigure]
  );

  return (
    <Box
      sx={{
        mt: 0.75,
        borderRadius: 1.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: 140,
        minWidth: 0,
        position: 'relative',
        '&:hover .chart-actions': { opacity: 1 },
      }}
    >
      <ThemedPlotlyFigure figure={compactFigure} height={140} onPlotlyInit={setChartEl} />
      <Box
        className="chart-actions"
        sx={{ position: 'absolute', top: 4, right: 4, opacity: 0, transition: 'opacity 0.15s' }}
      >
        <PlotlyChartActions
          chartEl={chartEl}
          figure={plotlyFigure}
          fileName={title || 'chart'}
          title={title || 'Chart Preview'}
        />
      </Box>
    </Box>
  );
};

const MAX_PREVIEW_ROWS = 5;
const MAX_PREVIEW_COLUMNS = 6;

const AgentTablePreviewDialog: React.FC<{ table: SectionTable; open: boolean; onClose: () => void }> = ({
  table,
  open,
  onClose,
}) => {
  const columns = table.columns || [];
  const rows = table.rows || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">{table.title || 'Table'}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {map(columns, (col) => (
                  <TableCell key={col.field} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {col.headerName || col.field}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {map(rows, (row, rowIdx) => (
                <TableRow key={rowIdx}>
                  {map(columns, (col) => (
                    <TableCell
                      key={col.field}
                      sx={{ whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {String(row[col.field] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {`${rows.length} rows, ${columns.length} columns`}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

const AgentTablePreview: React.FC<{ action: DisplayAgentAction }> = ({ action }) => {
  const tablePreview = action.tablePreview;
  const [dialogOpen, setDialogOpen] = React.useState(false);

  if (!tablePreview) {
    return null;
  }

  const columns = (tablePreview.columns || []).slice(0, MAX_PREVIEW_COLUMNS);
  const rows = (tablePreview.rows || []).slice(0, MAX_PREVIEW_ROWS);
  const totalRows = tablePreview.rows?.length ?? 0;
  const totalCols = tablePreview.columns?.length ?? 0;

  return (
    <>
      <Box
        sx={{
          mt: 0.75,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'relative',
          '&:hover .table-actions': { opacity: 1 },
        }}
      >
        <TableContainer sx={{ maxHeight: 160 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {map(columns, (col) => (
                  <TableCell
                    key={col.field}
                    sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, whiteSpace: 'nowrap' }}
                  >
                    {col.headerName || col.field}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {map(rows, (row, rowIdx) => (
                <TableRow key={rowIdx}>
                  {map(columns, (col) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        fontSize: '0.65rem',
                        py: 0.25,
                        whiteSpace: 'nowrap',
                        maxWidth: 120,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {String(row[col.field] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalRows > MAX_PREVIEW_ROWS || totalCols > MAX_PREVIEW_COLUMNS ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.25, display: 'block' }}>
            {`Showing ${Math.min(MAX_PREVIEW_ROWS, totalRows)} of ${totalRows} rows, ${Math.min(
              MAX_PREVIEW_COLUMNS,
              totalCols
            )} of ${totalCols} columns`}
          </Typography>
        ) : null}
        <Box
          className="table-actions"
          sx={{ position: 'absolute', top: 4, right: 4, opacity: 0, transition: 'opacity 0.15s' }}
        >
          <IconButton
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
          >
            <FullscreenRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <AgentTablePreviewDialog table={tablePreview} open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

const AgentActionRow: React.FC<{ action: DisplayAgentAction }> = ({ action }) => (
  <Stack spacing={0} sx={actionRowSx(action.status === 'running')}>
    <Stack direction="row" spacing={1} alignItems="center">
      <AgentActionIcon action={action} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {action.label}
        </Typography>
        {action.detail ? (
          <Box sx={actionDetailMarkdownSx}>
            <Markdown options={markdownOverrides}>{resolveOriginTemplate(action.detail)}</Markdown>
          </Box>
        ) : null}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {action.status === 'running' ? 'Running now' : 'Completed'}
          </Typography>
          {action.occurrenceCount > 1 ? (
            <Box
              sx={{
                px: 0.7,
                py: 0.1,
                borderRadius: 999,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06),
                border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                {`${action.occurrenceCount}x`}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Stack>
    {action.chartPreview ? <AgentChartPreview action={action} /> : null}
    {action.tablePreview ? <AgentTablePreview action={action} /> : null}
  </Stack>
);

const AgentParallelLanes: React.FC<{ actions: DisplayAgentAction[] }> = ({ actions }) => (
  <Box sx={{ pt: 0.75, pb: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
      Parallel branch
    </Typography>
    <Box
      aria-label="Parallel action lanes"
      role="list"
      sx={{
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: { xs: 'minmax(220px, 1fr)', sm: 'minmax(240px, 1fr)' },
        gap: 1,
        overflowX: 'auto',
        overscrollBehaviorX: 'contain',
        pb: 0.5,
        pr: 0.25,
        scrollbarWidth: 'thin',
      }}
    >
      {map(actions, (action) => (
        <Box
          key={action.id}
          role="listitem"
          sx={{
            minWidth: 0,
          }}
        >
          <Box sx={{ px: 1.25, py: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {getParallelLaneTitle(action)}
            </Typography>
          </Box>
          <AgentActionRow action={action} />
        </Box>
      ))}
    </Box>
  </Box>
);

const AgentPlanSection: React.FC<{ planText: string }> = ({ planText }) => (
  <Stack spacing={0.8} role="region" aria-label="Agent flow plan">
    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      Plan
    </Typography>
    <Box
      sx={{
        px: 0.5,
        py: 0.25,
        '& h1, & h2, & h3, & h4': {
          fontSize: '0.8rem',
          fontWeight: 700,
          mt: 0.5,
          mb: 0.25,
          color: 'text.primary',
        },
        '& p': {
          fontSize: '0.78rem',
          lineHeight: 1.55,
          color: 'text.primary',
          my: 0.25,
        },
        '& ul, & ol': {
          pl: 2,
          my: 0.25,
          '& li': {
            fontSize: '0.78rem',
            lineHeight: 1.55,
            color: 'text.primary',
            py: 0.1,
          },
        },
        '& strong': {
          fontWeight: 700,
        },
        '& em': {
          fontStyle: 'italic',
        },
      }}
    >
      <Markdown options={markdownOverrides}>{planText}</Markdown>
    </Box>
  </Stack>
);

const AgentProgressSection: React.FC<{
  currentStepText?: string | null;
  currentStepProgress?: number | null;
}> = ({ currentStepText, currentStepProgress }) => {
  if (!currentStepText) {
    return null;
  }

  return (
    <Stack spacing={0.8} role="region" aria-label="Current progress">
      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Current progress
      </Typography>
      {currentStepText ? (
        <Box sx={{ px: 0, py: 0.2 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
              In progress
            </Typography>
            {currentStepProgress ? (
              <Typography variant="caption" color="text.secondary">
                {`${currentStepProgress}%`}
              </Typography>
            ) : null}
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.45, color: 'text.primary', lineHeight: 1.55 }}>
            {currentStepText}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
};

export const AgentActionTimeline: React.FC<{
  actions: AgentStreamAction[];
  currentStatus?: string | null;
  currentProgress?: AgentStreamProgress | null;
  isStreaming?: boolean;
  planText?: string | null;
}> = ({ actions, currentStatus, currentProgress, isStreaming = false, planText }) => {
  const listRef = React.useRef<HTMLDivElement>(null);

  const displayActions = React.useMemo(
    () => (isStreaming ? expandActionsForStreaming(actions) : collapseRepeatedActions(actions)),
    [actions, isStreaming]
  );
  const hasPlan = Boolean(planText?.trim());
  const progressSummary = React.useMemo(
    () =>
      getReadableProgressSummary({
        agentStatus: currentStatus,
        agentProgress: currentProgress,
        agentActions: actions,
      }),
    [actions, currentProgress, currentStatus]
  );
  const { completedCount, statusText, totalCount } = getAgentActionSummary(actions, isStreaming);
  const { completedActions, runningActions } = splitAgentActionGroups(displayActions);

  React.useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [actions]);

  return (
    <Stack spacing={1} sx={actionCardSx}>
      <Box sx={{ px: 0, py: 0.15 }}>
        <Stack spacing={1.2}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            Agent Flow
          </Typography>
          {hasPlan ? <AgentPlanSection planText={planText!} /> : null}
          <AgentProgressSection
            currentStepText={progressSummary.currentStepText}
            currentStepProgress={progressSummary.currentStepProgress}
          />
          {!isEmpty(displayActions) ? (
            <Stack spacing={0.85}>
              <Box sx={{ pt: 0.1 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {statusText}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${completedCount}/${totalCount} done`}
                  </Typography>
                </Stack>
              </Box>
              <Stack ref={listRef} spacing={0.8} sx={actionListSx}>
                <Stack spacing={0.55}>
                  {map(completedActions, (action) => (
                    <AgentActionRow key={action.id} action={action} />
                  ))}
                  {size(runningActions) > 1 ? (
                    <AgentParallelLanes actions={runningActions} />
                  ) : (
                    map(runningActions, (action) => <AgentActionRow key={action.id} action={action} />)
                  )}
                </Stack>
              </Stack>
            </Stack>
          ) : null}
          {currentStatus && isEmpty(actions) && !progressSummary.currentStepText ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <TuneRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">
                {currentStatus}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
};
